import React, { ReactElement, useState } from 'react';
import JoinCheckboxFormGroup from '../../join/JoinCheckboxFormGroup';
import { DashlineSeparator } from '../../join/styledElements';
import { styledDropzoneStylesSmallPicture } from '../styledElements';
import { getImage } from '../../../modules/gallery/helpers';
import JoinPhotoUploadItem from '../../join/bulkUpload/JoinPhotoUploadItem';
import { FileType, PhotoFileType, PhotoListTypes } from '../../../modules/gallery/constants';
import colors from '../../../theme/colors';
import JoinEditablePhotoModal from '../../join/bulkUpload/JoinEditablePhotoModal';
import orderBy from 'lodash/orderBy';
import { preparePhotoMediaList } from '../../../modules/gallery/GalleryUpload/Photo/helpers';

interface ICompletePhotoUploadFormProps {
  handlePhotoUpload: (state: FileType[]) => void;
  label: string;
  onCancelUpload?: () => void;
  previewPhotos: PhotoListTypes;
}

const CompletePhotoUploadForm = ({
  handlePhotoUpload,
  onCancelUpload,
  label,
  previewPhotos,
}: ICompletePhotoUploadFormProps): ReactElement => {
  const [isLoading, setIsLoading] = useState(false);
  const [activeFile, setActiveFile] = useState<PhotoFileType | null>(null);
  const [fileParseError, setFileParseError] = useState<string | null>(null);

  const preparePhotos = async photos => {
    setIsLoading(true);
    const { results, errorMessage } = await preparePhotoMediaList(photos);

    setFileParseError(errorMessage);
    handlePhotoUpload([...previewPhotos, ...results]);
    setIsLoading(false);
  };

  const onRemove = (selectedPhotoIndex: number) => {
    const clonedPhotos = [...previewPhotos];
    const cleanPhotos = clonedPhotos.filter(photo => photo.index !== clonedPhotos[selectedPhotoIndex].index);

    if (cleanPhotos.length > 0) {
      cleanPhotos[0].primary = true;
      cleanPhotos[0].private = false;
      cleanPhotos.slice(1).forEach(photo => {
        photo.primary = false;
      });
    }

    handlePhotoUpload(cleanPhotos);
  };

  const onEdit = (file: PhotoFileType) => {
    setActiveFile(file);
  };

  const updateFileLists = async (file: File) => {
    if (activeFile === null) {
      return;
    }

    const updatedActiveFile = { ...activeFile };

    updatedActiveFile.edited = (await getImage(file)) as string;
    setActiveFile(updatedActiveFile);

    const updatePreviewPhotos = previewPhotos.map(photo =>
      photo.index === activeFile.index ? updatedActiveFile : photo
    );

    handlePhotoUpload(updatePreviewPhotos);
  };

  const updatePrivatePrimaryFile = ({ isPrimary, isPrivate }) => {
    if (activeFile === null) return;

    const updatedActiveFile = { ...activeFile };

    updatedActiveFile.primary = isPrimary;
    updatedActiveFile.private = isPrivate;

    setActiveFile(updatedActiveFile);

    const updatePreviewPhotos = previewPhotos.map(photo =>
      photo.index === activeFile.index ? updatedActiveFile : { ...photo, primary: false }
    );
    const orderdPreviewPhotos = orderBy(updatePreviewPhotos, ['primary'], ['desc']);

    handlePhotoUpload(orderdPreviewPhotos);
  };

  const previewPhotoUrl = (index: number) => {
    return previewPhotos[index] ? previewPhotos[index].edited : undefined;
  };

  const onPhotoUpload = photos => {
    preparePhotos(photos);
  };

  return (
    <>
      {isLoading && (
        <div
          style={{
            color: colors.red[500],
            fontSize: 14,
            fontWeight: 600,
            margin: '20px 0',
          }}
        >
          Hang tight, we’re preparing your photos...
        </div>
      )}

      {fileParseError && <div style={{ color: colors.red[500] }}>{fileParseError} </div>}

      <JoinCheckboxFormGroup label={label} isRequired>
        <div style={{ color: colors.light.red.primary, marginBottom: 10, marginTop: 10, fontStyle: 'italic' }}>
          For selfie verification, include at least one photo showing your face (can be set to private)
        </div>
        <div style={{ display: 'flex' }}>
          <JoinPhotoUploadItem
            key={0}
            photoUrl={previewPhotoUrl(0)}
            customStyledDropzoneStyles={styledDropzoneStylesSmallPicture}
            onCancelUpload={onCancelUpload}
            setFileList={onPhotoUpload}
            onRemove={() => onRemove(0)}
            onEdit={() => {
              onEdit(previewPhotos[0]);
            }}
            isLoading={isLoading}
            isSmallThumbnail
            countTotalPhotos={previewPhotos.length}
            isPrivate={previewPhotos[0]?.private ?? false}
          />
          <JoinPhotoUploadItem
            key={1}
            photoUrl={previewPhotoUrl(1)}
            customStyledDropzoneStyles={styledDropzoneStylesSmallPicture}
            onCancelUpload={onCancelUpload}
            setFileList={onPhotoUpload}
            onRemove={() => onRemove(1)}
            onEdit={() => onEdit(previewPhotos[1])}
            isLoading={isLoading}
            isSmallThumbnail
            countTotalPhotos={previewPhotos.length}
            isPrivate={previewPhotos[1]?.private ?? false}
          />
          <JoinPhotoUploadItem
            key={2}
            photoUrl={previewPhotoUrl(2)}
            customStyledDropzoneStyles={styledDropzoneStylesSmallPicture}
            onCancelUpload={onCancelUpload}
            setFileList={onPhotoUpload}
            onRemove={() => onRemove(2)}
            onEdit={() => onEdit(previewPhotos[2])}
            isLoading={isLoading}
            isSmallThumbnail
            countTotalPhotos={previewPhotos.length}
            isPrivate={previewPhotos[2]?.private ?? false}
          />
        </div>
        <div className="other-pic" style={{ display: 'flex' }}>
          <JoinPhotoUploadItem
            key={3}
            photoUrl={previewPhotoUrl(3)}
            customStyledDropzoneStyles={styledDropzoneStylesSmallPicture}
            onCancelUpload={onCancelUpload}
            setFileList={onPhotoUpload}
            onRemove={() => onRemove(3)}
            onEdit={() => onEdit(previewPhotos[3])}
            isLoading={isLoading}
            isSmallThumbnail
            countTotalPhotos={previewPhotos.length}
            isPrivate={previewPhotos[3]?.private ?? false}
          />
          <JoinPhotoUploadItem
            key={4}
            photoUrl={previewPhotoUrl(4)}
            customStyledDropzoneStyles={styledDropzoneStylesSmallPicture}
            onCancelUpload={onCancelUpload}
            setFileList={onPhotoUpload}
            onRemove={() => onRemove(4)}
            onEdit={() => onEdit(previewPhotos[4])}
            isLoading={isLoading}
            isSmallThumbnail
            countTotalPhotos={previewPhotos.length}
            isPrivate={previewPhotos[4]?.private ?? false}
          />
          <JoinPhotoUploadItem
            key={5}
            photoUrl={previewPhotoUrl(5)}
            customStyledDropzoneStyles={styledDropzoneStylesSmallPicture}
            onCancelUpload={onCancelUpload}
            setFileList={onPhotoUpload}
            onRemove={() => onRemove(5)}
            onEdit={() => onEdit(previewPhotos[5])}
            isLoading={isLoading}
            isSmallThumbnail
            countTotalPhotos={previewPhotos.length}
            isPrivate={previewPhotos[5]?.private ?? false}
          />
        </div>

        {previewPhotos.length > 6 &&
          (() => {
            const groupCount = Math.ceil((previewPhotos.length - 6) / 3);
            const items: JSX.Element[] = [];

            for (let groupIndex = 0; groupIndex < groupCount; groupIndex++) {
              const groupItems: JSX.Element[] = [];

              for (let itemIndex = 0; itemIndex < 3; itemIndex++) {
                const photoIndex = 6 + groupIndex * 3 + itemIndex;

                if (photoIndex < previewPhotos.length) {
                  groupItems.push(
                    <JoinPhotoUploadItem
                      key={photoIndex}
                      photoUrl={previewPhotoUrl(photoIndex)}
                      customStyledDropzoneStyles={styledDropzoneStylesSmallPicture}
                      customAddIconStyle={{
                        height: 32.98,
                        width: 37.102,
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                      }}
                      setFileList={onPhotoUpload}
                      onRemove={() => (previewPhotoUrl(photoIndex) ? onRemove(photoIndex) : undefined)}
                      onEdit={() => (previewPhotoUrl(photoIndex) ? onEdit(previewPhotos[photoIndex]) : undefined)}
                      isPrivate={previewPhotos[photoIndex]?.private ?? false}
                      isLoading={isLoading}
                      isSmallThumbnail
                      countTotalPhotos={previewPhotos.length}
                    />
                  );
                } else {
                  groupItems.push(
                    <JoinPhotoUploadItem
                      key={photoIndex}
                      containerStyle={
                        previewPhotos.length === 20
                          ? { visibility: 'visible', position: 'relative' }
                          : { visibility: 'hidden' }
                      }
                      photoItemStyle={{ visibility: 'hidden' }}
                      photoUrl={undefined}
                      customStyledDropzoneStyles={styledDropzoneStylesSmallPicture}
                      customAddIconStyle={{
                        height: 32.98,
                        width: 37.102,
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                      }}
                      isUploading={false}
                      setFileList={onPhotoUpload}
                      onRemove={() => undefined}
                      onEdit={() => undefined}
                      isLoading={false}
                      isSmallThumbnail
                      isMaxLength={previewPhotos.length === 20}
                      countTotalPhotos={0}
                    />
                  );
                }
              }

              items.push(
                <div className="other-pic" style={{ display: 'flex' }} key={groupIndex}>
                  {groupItems}
                </div>
              );
            }

            return items;
          })()}
      </JoinCheckboxFormGroup>
      <DashlineSeparator />

      <JoinEditablePhotoModal
        isOpen={activeFile ? true : false}
        onReset={() => setActiveFile(null)}
        activeFile={activeFile}
        onClose={() => setActiveFile(null)}
        updateFileLists={updateFileLists}
        updatePrivatePrimaryFile={updatePrivatePrimaryFile}
      />
    </>
  );
};

export default CompletePhotoUploadForm;
